/**
 * Request and Response Interceptors
 * Handles authentication, error handling, and request/response transformation
 */

import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { getAccessToken, isUsingSpoofAccessToken } from '@/lib/auth/accessTokenProvider';
import {
  isRefreshSuppressed,
  refreshAccessToken,
  signOutAndClearSession,
} from '@/lib/auth/authSessionService';
import { 
  ApiError, 
  NetworkError, 
  AuthenticationError, 
  AuthorizationError,
  NotFoundError,
  ValidationError 
} from './errors';

/**
 * Request interceptor to add authentication token
 */
export const setupRequestInterceptor = (instance: any) => {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );
};

/**
 * Response interceptor for error handling
 */
export const setupResponseInterceptor = (instance: any) => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Return full response object (includes status, headers, data, etc.)
      // Services can access response.data for the actual data
      return response;
    },
    async (error: AxiosError) => {
      // Handle aborted/cancelled requests silently
      if (error.code === 'ERR_CANCELED' || error.message?.includes('canceled') || error.message?.includes('aborted')) {
        // Return a special error that can be checked but won't trigger error handlers
        return Promise.reject(new Error('Request was cancelled'));
      }
      
      // Handle network errors
      if (!error.response) {
        // Check if it's an abort error
        if (error.message?.includes('aborted') || error.name === 'AbortError') {
          return Promise.reject(new Error('Request was cancelled'));
        }
        return Promise.reject(new NetworkError(error.message || 'Network error occurred'));
      }

      const { status, statusText, data } = error.response;
      const errorMessage = (data as any)?.error || (data as any)?.message || statusText || 'An error occurred';

      // Map HTTP status codes to custom error classes
      switch (status) {
        case 401: {
          if (isUsingSpoofAccessToken()) {
            return Promise.reject(new AuthenticationError(errorMessage, status, data));
          }

          // Attempt to refresh token and retry request once
          const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

          if (!originalRequest._retry) {
            originalRequest._retry = true;

            try {
              console.log('[Interceptor] 401 error - attempting to refresh session and retry request');

              // Shared single-flight refresh (safe under concurrent 401s / proactive refresh)
              const refreshedToken = await refreshAccessToken();

              if (!refreshedToken) {
                // Null can mean sign-out already suppressed refresh — do not sign out again
                // or overwrite an intentional reason with "expired".
                if (isRefreshSuppressed()) {
                  console.warn(
                    '[Interceptor] Refresh returned null under suppression — skipping sign-out'
                  );
                  return Promise.reject(new AuthenticationError(errorMessage, status, data));
                }
                console.error('[Interceptor] Session refresh failed — signing out');
                await signOutAndClearSession({ reason: 'expired' });
                return Promise.reject(new AuthenticationError(errorMessage, status, data));
              }

              // Same token as before means refresh was retryable/network — don't logout, don't loop
              const priorAuth = originalRequest.headers?.Authorization;
              const priorToken =
                typeof priorAuth === 'string' && priorAuth.toLowerCase().startsWith('bearer ')
                  ? priorAuth.slice(7).trim()
                  : null;
              if (priorToken && priorToken === refreshedToken) {
                console.warn('[Interceptor] Refresh did not yield a new token — failing request without sign-out');
                return Promise.reject(new AuthenticationError(errorMessage, status, data));
              }

              console.log('[Interceptor] Session refreshed successfully - retrying request with new token');

              originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
              return instance(originalRequest);
            } catch (refreshError) {
              console.error('[Interceptor] Error during token refresh:', refreshError);
              // Transient refresh errors should not hard-logout the user
              return Promise.reject(new AuthenticationError(errorMessage, status, data));
            }
          }

          // Already retried once, fail with authentication error
          console.log('[Interceptor] Request already retried - authentication failed');
          return Promise.reject(new AuthenticationError(errorMessage, status, data));
        }

        case 403:
          return Promise.reject(new AuthorizationError(errorMessage, status, data));
        case 404:
          return Promise.reject(new NotFoundError(errorMessage, data));
        case 400:
          return Promise.reject(new ValidationError(errorMessage, data));
        default:
          return Promise.reject(new ApiError(errorMessage, status, statusText, data));
      }
    }
  );
};

